import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Button,
  DatePicker,
  Select,
  Alert,
  Spin,
  Card,
  Statistic,
  Table,
  Tooltip,
  Tag,
  Space,
  Radio,
  Row,
  Col,
} from "antd";
import {
  FileExcelOutlined,
  FilePdfOutlined,
  BarChartOutlined,
  CalendarOutlined,
  UserOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  reporteNovedadesApi,
  campusApi,
  dependenciasApi,
  tiposNovedadesApi,
  empleadosApi,
} from "../../services/api";
import moment from "moment";
import "moment/locale/es";
import locale from "antd/es/date-picker/locale/es_ES";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const { RangePicker } = DatePicker;
const { Option } = Select;

const ReporteNovedadesModal = ({ visible, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [campusOptions, setCampusOptions] = useState([]);
  const [dependenciasOptions, setDependenciasOptions] = useState([]);
  const [tiposNovedadOptions, setTiposNovedadOptions] = useState([]);
  const [empleadosOptions, setEmpleadosOptions] = useState([]);
  const [tipoReporte, setTipoReporte] = useState('grupal'); // 'grupal' o 'empleado'

  // Cargar opciones para los selects
  useEffect(() => {
    const cargarDatos = async () => {
      setLoading(true);
      try {
        const [campusRes, dependenciasRes, tiposNovedadRes, empleadosRes] = await Promise.all(
          [
            campusApi.getAll(),
            dependenciasApi.getAll(),
            tiposNovedadesApi.getAll(),
            empleadosApi.getAll(),
          ]
        );

        // Procesar datos de campus
        let campusData = [];
        if (campusRes.data && Array.isArray(campusRes.data)) {
          campusData = campusRes.data;
        } else if (campusRes.data && campusRes.data.campus) {
          campusData = campusRes.data.campus;
        }
        setCampusOptions(campusData);

        // Procesar datos de dependencias
        let dependenciasData = [];
        if (dependenciasRes.data && Array.isArray(dependenciasRes.data)) {
          dependenciasData = dependenciasRes.data;
        } else if (dependenciasRes.data && dependenciasRes.data.dependencias) {
          dependenciasData = dependenciasRes.data.dependencias;
        }
        setDependenciasOptions(dependenciasData);
        // Procesar datos de tipos de novedad
        let tiposData = [];
        if (tiposNovedadRes.data && tiposNovedadRes.data.tipos_novedades) {
          tiposData = tiposNovedadRes.data.tipos_novedades;
        } else if (Array.isArray(tiposNovedadRes.data)) {
          tiposData = tiposNovedadRes.data;
        } else if (tiposNovedadRes.data && tiposNovedadRes.data.tipos) {
          tiposData = tiposNovedadRes.data.tipos;
        }
        setTiposNovedadOptions(tiposData);

        // Procesar datos de empleados
        let empleadosData = [];
        if (empleadosRes.data && Array.isArray(empleadosRes.data)) {
          empleadosData = empleadosRes.data;
        } else if (empleadosRes.data && empleadosRes.data.empleados) {
          empleadosData = empleadosRes.data.empleados;
        }
        setEmpleadosOptions(empleadosData);
      } catch (error) {
        console.error("Error al cargar opciones para el reporte:", error);
        toast.error("Error al cargar las opciones del formulario de reporte");
      } finally {
        setLoading(false);
      }
    };

    if (visible) {
      cargarDatos();
    }
  }, [visible]);

  // Limpiar datos al cerrar el modal
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setReportData(null);
      setTipoReporte('grupal');
    }
  }, [visible, form]);
  const handleGenerateReport = async (values) => {
    setGeneratingReport(true);
    try {
      // Preparar los datos del formulario
      const { dateRange, campus_id, dependencia_id, tipo_novedad_id } = values;

      const reportParams = {
        fecha_inicio: dateRange[0].format("YYYY-MM-DD"),
        fecha_fin: dateRange[1].format("YYYY-MM-DD"),
      };

      // Añadir parámetros opcionales solo si tienen valor
      if (campus_id) reportParams.campus_id = campus_id;
      if (dependencia_id) reportParams.dependencia_id = dependencia_id;
      if (tipo_novedad_id) reportParams.tipo_novedad_id = tipo_novedad_id;

      // Llamada al endpoint correcto
      const response = await reporteNovedadesApi.getReport(reportParams);

      if (response.data && response.data.status) {
        setReportData(response.data);
        toast.success("Reporte generado exitosamente");
      } else {
        toast.warning("No se encontraron datos para el reporte");
      }
    } catch (error) {
      console.error("Error al generar reporte:", error);
      toast.error(
        "Error al generar el reporte. Verifique los parámetros e intente nuevamente."
      );
    } finally {
      setGeneratingReport(false);
    }
  };

  // Función para generar PDF por empleado
  const handleGeneratePdfEmpleado = async (values) => {
    setGeneratingPdf(true);
    try {
      const { dateRange, empleado_id } = values;

      if (!empleado_id) {
        toast.error("Debe seleccionar un empleado para generar el PDF");
        return;
      }

      const reportParams = {
        empleado_id: empleado_id,
        fecha_inicio: dateRange[0].format("YYYY-MM-DD"),
        fecha_fin: dateRange[1].format("YYYY-MM-DD"),
      };

      const response = await reporteNovedadesApi.getReportPdfEmpleado(reportParams);
      
      // Crear blob y descargar PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Obtener nombre del empleado para el archivo
      const empleado = empleadosOptions.find(emp => emp.id === empleado_id);
      const nombreEmpleado = empleado ? empleado.nombre.replace(/\s+/g, '_') : 'empleado';
      const fechaInicio = dateRange[0].format("YYYYMMDD");
      const fechaFin = dateRange[1].format("YYYYMMDD");
      const fileName = `reporte_${nombreEmpleado}_${fechaInicio}_${fechaFin}.pdf`;
      
      // Crear enlace temporal para descarga
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF generado y descargado exitosamente");
    } catch (error) {
      console.error("Error al generar PDF por empleado:", error);
      toast.error("Error al generar el PDF. Verifique los parámetros e intente nuevamente.");
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Función para generar PDF grupal
  const handleGeneratePdfGrupal = async () => {
    if (!reportData || !reportData.novedades || reportData.novedades.length === 0) {
      toast.warning("No hay datos para generar el PDF");
      return;
    }

    setGeneratingPdf(true);
    try {
      const values = form.getFieldsValue();
      const { dateRange, campus_id, dependencia_id, tipo_novedad_id } = values;

      const reportParams = {
        fecha_inicio: dateRange[0].format("YYYY-MM-DD"),
        fecha_fin: dateRange[1].format("YYYY-MM-DD"),
      };

      // Añadir parámetros opcionales solo si tienen valor
      if (campus_id) reportParams.campus_id = campus_id;
      if (dependencia_id) reportParams.dependencia_id = dependencia_id;
      if (tipo_novedad_id) reportParams.tipo_novedad_id = tipo_novedad_id;

      const response = await reporteNovedadesApi.getReportpdf(reportParams);
      
      // Crear blob y descargar PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      const fechaInicio = dateRange[0].format("YYYYMMDD");
      const fechaFin = dateRange[1].format("YYYYMMDD");
      const fileName = `reporte_grupal_${fechaInicio}_${fechaFin}.pdf`;
      
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF grupal generado y descargado exitosamente");
    } catch (error) {
      console.error("Error al generar PDF grupal:", error);
      toast.error("Error al generar el PDF grupal.");
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Columnas para la tabla de resultados
  const columns = [
    {
      title: "Fecha Creación",
      dataIndex: "fecha",
      key: "fecha",
      render: (fecha) => moment(fecha).format("DD/MM/YYYY"),
    },
    {
      title: "Fecha Inicio",
      dataIndex: "fecha_ini",
      key: "fecha_ini",
      render: (fecha_ini) => moment(fecha_ini).format("DD/MM/YYYY"),
    },
    {
      title: "Fecha Fin",
      dataIndex: "fecha_fin",
      key: "fecha_fin",
      render: (fecha_fin) => moment(fecha_fin).format("DD/MM/YYYY"),
    },
    {
      title: "Empleado",
      dataIndex: ["empleado", "nombre"],
      key: "empleado",
      ellipsis: true,
    },
    {
      title: "Cédula",
      dataIndex: ["empleado", "cedula"],
      key: "cedula",
    },
    {
      title: "Campus",
      dataIndex: ["empleado", "campus", "campus"],
      key: "campus",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Dependencia",
      dataIndex: ["empleado", "dependencia", "dependencia"],
      key: "dependencia",
      ellipsis: true,
    },
    {
      title: "Tipo Novedad",
      dataIndex: ["tipo_novedad", "novedad"],
      key: "tipo_novedad",
      render: (text) => <Tag color="green">{text}</Tag>,
    },
    {
      title: "Días",
      dataIndex: "dias",
      key: "dias",
      align: "center",
    },
    {
      title: "Horas",
      dataIndex: "horas",
      key: "horas",
      align: "center",
    },
    {
      title: "Minutos",
      dataIndex: "minutos",
      key: "minutos",
      align: "center",
    },
    {
      title: "Nómina",
      dataIndex: "nomina",
      key: "nomina",
      align: "center",
      render: (afecta) => (
        <Tag color={afecta ? "success" : "default"}>{afecta ? "SI" : "NO"}</Tag>
      ),
    },
    {
      title: "Observaciones",
      dataIndex: "observaciones",
      key: "observaciones",
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text}</span>
        </Tooltip>
      ),
    },
  ];

  // Renderizar estadísticas
  const renderEstadisticas = () => {
    if (!reportData || !reportData.estadisticas) return null;

    const { total, por_tipo } = reportData.estadisticas;

    return (
    
        <></>
    );
  };
  // Función para exportar a Excel
  const handleExportExcel = () => {
    if (
      !reportData ||
      !reportData.novedades ||
      reportData.novedades.length === 0
    ) {
      toast.warning("No hay datos para exportar a Excel");
      return;
    }

    try {
      // Preparar los datos formateados para Excel
      const excelData = reportData.novedades.map((item) => ({
        "Fecha Creación": moment(item.fecha).format("DD/MM/YYYY"),
        "Fecha Inicio": moment(item.fecha_ini).format("DD/MM/YYYY"),
        "Fecha Fin": moment(item.fecha_fin).format("DD/MM/YYYY"),
        "Nombre Empleado": item.empleado?.nombre || "N/A",
        Cédula: item.empleado?.cedula || "N/A",
        Campus: item.empleado?.campus?.campus || "N/A",
        Cargo: item.empleado?.cargo || "N/A",
        Dependencia: item.empleado?.dependencia?.dependencia || "N/A",
        "Tipo Novedad": item.tipo_novedad?.novedad || "N/A",
        Días: item.dias || 0,
        Horas: item.horas || 0,
        Minutos: item.minutos || 0,
        "Afecta Nómina": item.nomina ? "SI" : "NO",
        Curso: item.curso || "N/A",
        Horario: item.horario || "N/A",
        Grupo: item.grupo || "N/A",
        Observaciones: item.observaciones || "",
        "Creado por": item.usuario?.name || "N/A",
      }));

      // Crear la hoja de Excel con los datos formateados
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Reporte Novedades");

      // Añadir información estadística en una segunda hoja
      if (reportData.estadisticas && reportData.estadisticas.por_tipo) {
        const estadisticas = [
          {
            Estadística: "Total Novedades",
            Valor: reportData.estadisticas.total,
          },
        ];

        // Añadir estadísticas por tipo
        Object.keys(reportData.estadisticas.por_tipo).forEach((key) => {
          estadisticas.push({
            Estadística: `Tipo: ${reportData.estadisticas.por_tipo[key].tipo}`,
            Valor: reportData.estadisticas.por_tipo[key].cantidad,
          });
        });

        const statsSheet = XLSX.utils.json_to_sheet(estadisticas);
        XLSX.utils.book_append_sheet(workbook, statsSheet, "Estadísticas");
      }

      // Generar archivo Excel
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const file = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });
      // Descargar archivo con nombre personalizado (fecha_inicio-fecha_fin)
      const fechaInicio = form
        .getFieldValue("dateRange")?.[0]
        ?.format("YYYYMMDD");
      const fechaFin = form.getFieldValue("dateRange")?.[1]?.format("YYYYMMDD");
      const fileName = `reporte_novedades_${fechaInicio || ""}_${
        fechaFin || ""
      }_${moment().format("HHmmss")}.xlsx`;
      saveAs(file, fileName);
      toast.success("Reporte exportado exitosamente a Excel");
    } catch (error) {
      console.error("Error al exportar a Excel:", error);
      toast.error("Error al generar el archivo Excel");
    }
  };

  // Botones para exportar
  const renderExportButtons = () => (
    <Space className="mt-4">
      <Button
        icon={<FileExcelOutlined />}
        className="bg-green-50 text-green-700 border-green-500 hover:bg-green-100"
        onClick={handleExportExcel}
      >
        Exportar a Excel
      </Button>
      <Button
        icon={<FilePdfOutlined />}
        className="bg-red-50 text-red-700 border-red-500 hover:bg-red-100"
        onClick={handleGeneratePdfGrupal}
        loading={generatingPdf}
      >
        Exportar a PDF
      </Button>
    </Space>
  );

  return (
    <Modal
      title={
        <div className="text-xl font-bold">
          Generación de Reportes de Novedades
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={reportData && tipoReporte === 'grupal' ? 1000 : 600}
      destroyOnClose
    >
      <Spin spinning={loading} tip="Cargando opciones...">
        <Form
          form={form}
          layout="vertical"
          onFinish={tipoReporte === 'grupal' ? handleGenerateReport : handleGeneratePdfEmpleado}
          initialValues={{
            tipoReporte: 'grupal'
          }}
        >
          {/* Selector de tipo de reporte */}
          <Form.Item label="Tipo de Reporte">
            <Radio.Group 
              value={tipoReporte} 
              onChange={(e) => setTipoReporte(e.target.value)}
              className="mb-4"
            >
              <Radio.Button value="grupal">
                <TeamOutlined className="mr-2" />
                Reporte Grupal
              </Radio.Button>
              <Radio.Button value="empleado">
                <UserOutlined className="mr-2" />
                Reporte por Empleado (PDF)
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="dateRange"
            label="Rango de Fechas"
            rules={[
              {
                required: true,
                message: "Por favor seleccione el rango de fechas",
              },
            ]}
          >
            <RangePicker
              className="w-full"
              format="DD/MM/YYYY"
              locale={locale}
              placeholder={["Fecha inicio", "Fecha fin"]}
            />
          </Form.Item>

          {/* Campos condicionales según el tipo de reporte */}
          {tipoReporte === 'empleado' ? (
            <Form.Item
              name="empleado_id"
              label="Empleado"
              rules={[
                {
                  required: true,
                  message: "Por favor seleccione un empleado",
                },
              ]}
            >
              <Select
                placeholder="Seleccione el empleado"
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {empleadosOptions.map((empleado) => (
                  <Option key={empleado.id} value={empleado.id}>
                    {`${empleado.nombre} - ${empleado.cedula}`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <>
              <Form.Item name="campus_id" label="Campus">
                <Select placeholder="Seleccione el campus (opcional)" allowClear>
                  {campusOptions.map((campus) => (
                    <Option key={campus.id} value={campus.id}>
                      {campus.campus}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="dependencia_id" label="Dependencia">
                <Select
                  placeholder="Seleccione la dependencia (opcional)"
                  allowClear
                >
                  {dependenciasOptions.map((dependencia) => (
                    <Option key={dependencia.id} value={dependencia.id}>
                      {dependencia.dependencia}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="tipo_novedad_id" label="Tipo de Novedad">
                <Select
                  placeholder="Seleccione el tipo de novedad (opcional)"
                  allowClear
                >
                  {tiposNovedadOptions.map((tipo) => (
                    <Option key={tipo.id} value={tipo.id}>
                      {tipo.novedad}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}

          <Alert
            message="Nota"
            description={
              tipoReporte === 'empleado'
                ? "Para el reporte por empleado, debe seleccionar las fechas y el empleado. El resultado será un PDF descargable."
                : "Solo las fechas de inicio y fin son obligatorias. Los demás filtros son opcionales."
            }
            type="info"
            showIcon
            className="mb-4"
          />

          <div className="flex justify-end">
            <Button onClick={onCancel} className="mr-2">
              Cancelar
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={tipoReporte === 'grupal' ? generatingReport : generatingPdf}
              disabled={loading}
              icon={tipoReporte === 'grupal' ? <BarChartOutlined /> : <FilePdfOutlined />}
            >
              {tipoReporte === 'grupal' ? 'Generar Reporte' : 'Generar PDF'}
            </Button>
          </div>
        </Form>
      </Spin>

      {reportData && tipoReporte === 'grupal' && (
        <div className="mt-8 border-t pt-6">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <CalendarOutlined className="mr-2" />
            Resultados del Reporte
          </h2>

          {reportData.novedades && reportData.novedades.length > 0 ? (
            <>
              {renderEstadisticas()}

              <Table
                columns={columns}
                dataSource={reportData.novedades}
                rowKey="id"
                scroll={{ x: "max-content" }}
                pagination={{ pageSize: 5 }}
                className="mt-4"
              />

              {renderExportButtons()}
            </>
          ) : (
            <Alert
              message="Sin resultados"
              description="No se encontraron novedades para los criterios seleccionados."
              type="warning"
              showIcon
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default ReporteNovedadesModal;
